import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  getTransactions,
  createTransaction,
  updateTransaction,
  deleteTransaction,
  getCategories,
  formatCurrency,
  formatDate,
  downloadExport,
  downloadPdfReport,
  MONTHS,
  TRANSACTION_TYPES,
  TYPE_COLORS
} from '../lib/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import {
  Plus,
  Search,
  Filter,
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Edit,
  Trash2,
  ArrowUpRight,
  ArrowDownRight,
  Download,
  FileText,
  FileSpreadsheet,
  PiggyBank,
  CreditCard,
  CheckCircle2,
  Lock,
  Users as UsersIcon,
  Home as HomeIcon,
} from 'lucide-react';
import MemberPicker from '../components/MemberPicker';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { createCategory } from '../lib/api';

const TYPE_TILES = [
  { value: 'REDDITO',  label: 'Entrata',   sign: '+',  hint: 'Es. stipendio, regalo, bonus',  icon: ArrowUpRight,    color: 'emerald' },
  { value: 'SPESE',    label: 'Uscita',    sign: '−',  hint: 'Es. spesa, bolletta, ristorante', icon: ArrowDownRight, color: 'red' },
  { value: 'RISPARMI', label: 'Risparmio', sign: '−',  hint: 'Soldi messi da parte',           icon: PiggyBank,       color: 'blue' },
  { value: 'DEBITI',   label: 'Debito',    sign: '−',  hint: 'Rate mutuo, prestiti, ecc.',     icon: CreditCard,      color: 'orange' },
];

const TILE_STYLES = {
  emerald: { active: 'bg-emerald-500 text-white border-emerald-500', idle: 'border-emerald-200 dark:border-emerald-900/40 hover:bg-emerald-50 dark:hover:bg-emerald-900/20', accent: 'text-emerald-600 dark:text-emerald-400' },
  red:     { active: 'bg-red-500 text-white border-red-500',         idle: 'border-red-200 dark:border-red-900/40 hover:bg-red-50 dark:hover:bg-red-900/20',                accent: 'text-red-500' },
  blue:    { active: 'bg-blue-500 text-white border-blue-500',       idle: 'border-blue-200 dark:border-blue-900/40 hover:bg-blue-50 dark:hover:bg-blue-900/20',             accent: 'text-blue-500' },
  orange:  { active: 'bg-orange-500 text-white border-orange-500',   idle: 'border-orange-200 dark:border-orange-900/40 hover:bg-orange-50 dark:hover:bg-orange-900/20',     accent: 'text-orange-500' },
};

const CREATE_CATEGORY_VALUE = '__create_new__';

const TransactionsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  
  // Form state
  const [formDate, setFormDate] = useState(new Date());
  const [formType, setFormType] = useState('SPESE');
  const [formCategory, setFormCategory] = useState('');
  const [formAmount, setFormAmount] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formPrivate, setFormPrivate] = useState(false);
  const [formAssignees, setFormAssignees] = useState([]);
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Inline-create category state
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategorySaving, setNewCategorySaving] = useState(false);

  const handleCreateCategoryInline = async () => {
    const name = newCategoryName.trim();
    if (!name) return;
    setNewCategorySaving(true);
    try {
      const res = await createCategory({ name, type: formType, icon: 'coins' });
      const newCat = res.data;
      // Aggiorna lista locale e seleziona la nuova
      setCategories((prev) => [...prev, newCat]);
      setFormCategory(newCat.id);
      setNewCategoryName('');
      setCreatingCategory(false);
      toast.success(`Categoria "${name}" creata`);
    } catch (e) {
      toast.error('Errore creazione categoria');
    } finally {
      setNewCategorySaving(false);
    }
  };

  useEffect(() => {
    loadData();
    if (searchParams.get('add') === 'true') {
      openAddDialog({ privateDefault: searchParams.get('private') === 'true' });
      setSearchParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, year, typeFilter === 'ALL' ? '' : typeFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [transRes, catRes] = await Promise.all([
        getTransactions({ month, year, type: typeFilter === 'ALL' ? undefined : typeFilter }),
        getCategories()
      ]);
      setTransactions(transRes.data);
      setCategories(catRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const changeMonth = (delta) => {
    let newMonth = month + delta;
    let newYear = year;
    if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    } else if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    }
    setMonth(newMonth);
    setYear(newYear);
  };

  const openAddDialog = (opts = {}) => {
    setEditingTransaction(null);
    setFormDate(new Date());
    setFormType('SPESE');
    setFormCategory('');
    setFormAmount('');
    setFormDescription('');
    setFormPrivate(!!opts.privateDefault);
    setFormAssignees([]);
    setCreatingCategory(false);
    setNewCategoryName('');
    setDialogOpen(true);
  };

  const openEditDialog = (transaction) => {
    setEditingTransaction(transaction);
    setFormDate(new Date(transaction.date));
    setFormType(transaction.type);
    setFormCategory(transaction.category_id);
    setFormAmount(transaction.amount.toString());
    setFormDescription(transaction.description || '');
    setFormPrivate(!!transaction.is_private);
    setFormAssignees(transaction.assignees || []);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formCategory || !formAmount) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    const data = {
      date: format(formDate, 'yyyy-MM-dd'),
      type: formType,
      category_id: formCategory,
      amount: parseFloat(formAmount),
      description: formDescription,
      is_private: formPrivate,
      assignees: formAssignees.length > 0 ? formAssignees : null,
    };

    try {
      if (editingTransaction) {
        await updateTransaction(editingTransaction.id, data);
        toast.success('Transazione aggiornata');
      } else {
        await createTransaction(data);
        toast.success('Transazione aggiunta');
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleDelete = async () => {
    if (!transactionToDelete) return;
    
    try {
      await deleteTransaction(transactionToDelete.id);
      toast.success('Transazione eliminata');
      setDeleteDialogOpen(false);
      setTransactionToDelete(null);
      loadData();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const filteredTransactions = transactions.filter(t => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        t.category_name?.toLowerCase().includes(search) ||
        t.description?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const categoriesByType = categories.filter(c => c.type === formType);

  // Calculate totals
  const totals = filteredTransactions.reduce((acc, t) => {
    acc[t.type] = (acc[t.type] || 0) + t.amount;
    return acc;
  }, {});

  return (
    <div className="space-y-6 animate-in" data-testid="transactions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Transazioni</h1>
          <p className="text-muted-foreground">Gestisci entrate e uscite</p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="export-btn">
                <Download className="w-4 h-4 mr-2" /> Esporta
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={async () => {
                try {
                  const blob = await downloadExport('csv', month, year);
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url; a.download = `transazioni-${year}-${String(month).padStart(2, '0')}.csv`;
                  a.click(); URL.revokeObjectURL(url);
                  toast.success('Export CSV pronto');
                } catch { toast.error('Errore export'); }
              }} data-testid="export-csv-btn">
                <FileSpreadsheet className="w-4 h-4 mr-2" /> CSV ({MONTHS[month - 1]})
              </DropdownMenuItem>
              <DropdownMenuItem onClick={async () => {
                try {
                  const blob = await downloadExport('xlsx', month, year);
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url; a.download = `transazioni-${year}-${String(month).padStart(2, '0')}.xlsx`;
                  a.click(); URL.revokeObjectURL(url);
                  toast.success('Export Excel pronto');
                } catch { toast.error('Errore export'); }
              }} data-testid="export-xlsx-btn">
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel ({MONTHS[month - 1]})
              </DropdownMenuItem>
              <DropdownMenuItem onClick={async () => {
                try {
                  const blob = await downloadPdfReport('monthly', month, year);
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url; a.download = `report-${year}-${String(month).padStart(2, '0')}.pdf`;
                  a.click(); URL.revokeObjectURL(url);
                  toast.success('Report PDF pronto');
                } catch { toast.error('Errore PDF'); }
              }} data-testid="export-pdf-btn">
                <FileText className="w-4 h-4 mr-2" /> Report PDF mese
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={openAddDialog} data-testid="add-transaction-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuova
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Month Selector */}
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={() => changeMonth(-1)} data-testid="trans-prev-month">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="px-3 py-2 bg-muted rounded-lg min-w-[150px] text-center font-medium">
                {MONTHS[month - 1]} {year}
              </div>
              <Button variant="outline" size="icon" onClick={() => changeMonth(1)} data-testid="trans-next-month">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {/* Type Filter */}
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[180px]" data-testid="type-filter-select">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Tutti i tipi" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Tutti i tipi</SelectItem>
                {TRANSACTION_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Cerca transazioni..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="search-transactions-input"
              />
            </div>
          </div>

          {/* Totals Summary */}
          <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t">
            {Object.entries(totals).map(([type, amount]) => (
              <div key={type} className={`px-3 py-1.5 rounded-lg ${TYPE_COLORS[type]?.bg}`}>
                <span className={`text-sm font-medium ${TYPE_COLORS[type]?.text}`}>
                  {type}: {formatCurrency(amount)}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Transactions List */}
      <Card data-testid="transactions-list">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">Caricamento...</div>
          ) : filteredTransactions.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              Nessuna transazione trovata
            </div>
          ) : (
            <div className="divide-y">
              {filteredTransactions.map((t) => (
                <div 
                  key={t.id} 
                  className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
                  data-testid={`transaction-row-${t.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${TYPE_COLORS[t.type]?.bg}`}>
                      {t.type === 'REDDITO' ? (
                        <ArrowUpRight className={`w-5 h-5 ${TYPE_COLORS[t.type]?.text}`} />
                      ) : (
                        <ArrowDownRight className={`w-5 h-5 ${TYPE_COLORS[t.type]?.text}`} />
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium truncate">{t.category_name}</p>
                        {t.is_private && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                            <Lock className="w-2.5 h-2.5" /> privata
                          </span>
                        )}
                        {t.assignee_names && t.assignee_names.length > 0 && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                            <UsersIcon className="w-2.5 h-2.5" /> {t.assignee_names.join(' · ')}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>{formatDate(t.date)}</span>
                        {t.description && <span>• {t.description}</span>}
                        {t.created_by_name && (
                          <span className="text-[11px]">• inserita da {t.created_by_name}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`font-semibold tabular-nums ${
                      t.type === 'REDDITO' ? 'text-emerald-600 dark:text-emerald-400' : ''
                    }`}>
                      {t.type === 'REDDITO' ? '+' : '-'}{formatCurrency(t.amount)}
                    </span>
                    <div className="flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => openEditDialog(t)}
                        data-testid={`edit-transaction-${t.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => { setTransactionToDelete(t); setDeleteDialogOpen(true); }}
                        data-testid={`delete-transaction-${t.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="transaction-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingTransaction ? 'Modifica Transazione' : 'Nuova Transazione'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Date */}
            <div className="space-y-2">
              <Label>Data</Label>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start" data-testid="date-picker-btn">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {format(formDate, 'dd MMMM yyyy', { locale: it })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formDate}
                    onSelect={(date) => { setFormDate(date || new Date()); setCalendarOpen(false); }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Visibilità: famiglia / privato */}
            <div className="space-y-2">
              <Label>Visibilità</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setFormPrivate(false)}
                  className={`rounded-xl border-2 p-3 transition-all flex items-center gap-3 ${
                    !formPrivate
                      ? 'bg-primary/10 border-primary text-foreground'
                      : 'bg-card border-border hover:border-primary/40'
                  }`}
                  data-testid="visibility-family"
                >
                  <UsersIcon className={`w-5 h-5 ${!formPrivate ? 'text-primary' : 'text-muted-foreground'}`} strokeWidth={1.75} />
                  <div className="text-left">
                    <p className="text-sm font-semibold">Famiglia</p>
                    <p className="text-[11px] text-muted-foreground">Visibile a tutti i membri</p>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setFormPrivate(true)}
                  className={`rounded-xl border-2 p-3 transition-all flex items-center gap-3 ${
                    formPrivate
                      ? 'bg-amber-100 dark:bg-amber-900/30 border-amber-500 text-foreground'
                      : 'bg-card border-border hover:border-amber-400/40'
                  }`}
                  data-testid="visibility-private"
                >
                  <Lock className={`w-5 h-5 ${formPrivate ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}`} strokeWidth={1.75} />
                  <div className="text-left">
                    <p className="text-sm font-semibold">Privato</p>
                    <p className="text-[11px] text-muted-foreground">Solo io vedo questa</p>
                  </div>
                </button>
              </div>
            </div>

            {/* Type — 4 macro tiles */}
            <div className="space-y-2">
              <Label>Che tipo di movimento?</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="form-type-tiles">
                {TYPE_TILES.map((t) => {
                  const isActive = formType === t.value;
                  const styles = TILE_STYLES[t.color];
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => {
                        setFormType(t.value);
                        setFormCategory('');
                        setCreatingCategory(false);
                      }}
                      data-testid={`type-tile-${t.value}`}
                      className={`relative rounded-xl border-2 p-3 transition-all flex flex-col items-center gap-1.5 ${
                        isActive ? styles.active : `bg-card ${styles.idle}`
                      }`}
                    >
                      <div className="flex items-center gap-1.5">
                        <span className={`text-2xl font-extrabold leading-none ${isActive ? '' : styles.accent}`}>{t.sign}</span>
                        <Icon className={`w-5 h-5 ${isActive ? '' : styles.accent}`} strokeWidth={2} />
                      </div>
                      <span className="text-sm font-semibold">{t.label}</span>
                      <span className={`text-[10px] leading-tight text-center ${isActive ? 'opacity-90' : 'text-muted-foreground'}`}>
                        {t.hint}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Category */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Categoria</Label>
                {!creatingCategory && (
                  <button
                    type="button"
                    onClick={() => setCreatingCategory(true)}
                    className="text-xs font-medium text-primary hover:underline inline-flex items-center gap-1"
                    data-testid="create-category-toggle"
                  >
                    <Plus className="w-3 h-3" /> Crea nuova
                  </button>
                )}
              </div>

              {creatingCategory ? (
                <div className="flex items-center gap-2">
                  <Input
                    placeholder={`Nome categoria (${TYPE_TILES.find((t) => t.value === formType)?.label})`}
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); handleCreateCategoryInline(); }
                      if (e.key === 'Escape') { setCreatingCategory(false); setNewCategoryName(''); }
                    }}
                    autoFocus
                    data-testid="new-category-input"
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleCreateCategoryInline}
                    disabled={!newCategoryName.trim() || newCategorySaving}
                    data-testid="new-category-save"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Crea
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => { setCreatingCategory(false); setNewCategoryName(''); }}
                  >
                    Annulla
                  </Button>
                </div>
              ) : (
                <Select
                  value={formCategory}
                  onValueChange={(v) => {
                    if (v === CREATE_CATEGORY_VALUE) {
                      setCreatingCategory(true);
                    } else {
                      setFormCategory(v);
                    }
                  }}
                >
                  <SelectTrigger data-testid="form-category-select">
                    <SelectValue placeholder={categoriesByType.length === 0 ? 'Nessuna categoria — creane una' : 'Seleziona categoria'} />
                  </SelectTrigger>
                  <SelectContent>
                    {categoriesByType.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                    {categoriesByType.length > 0 && (
                      <div className="my-1 border-t" />
                    )}
                    <SelectItem value={CREATE_CATEGORY_VALUE} className="text-primary">
                      <span className="inline-flex items-center gap-1">
                        <Plus className="w-3.5 h-3.5" /> Crea nuova categoria…
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Amount */}
            <div className="space-y-2">
              <Label>Importo (€)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formAmount}
                onChange={(e) => setFormAmount(e.target.value)}
                placeholder="0.00"
                required
                data-testid="form-amount-input"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>Descrizione (opzionale)</Label>
              <Input
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="Aggiungi una nota..."
                data-testid="form-description-input"
              />
            </div>

            {/* Intestatari */}
            <MemberPicker
              value={formAssignees}
              onChange={setFormAssignees}
              label="A chi è intestato?"
              help="Seleziona uno o più membri se questo movimento riguarda persone specifiche (es. stipendio di Marco, mutuo split). Lascia vuoto se è di tutta la famiglia."
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annulla
              </Button>
              <Button type="submit" data-testid="save-transaction-btn">
                {editingTransaction ? 'Salva Modifiche' : 'Aggiungi'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle>Conferma Eliminazione</DialogTitle>
          </DialogHeader>
          <p>Sei sicuro di voler eliminare questa transazione?</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Annulla
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">
              Elimina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TransactionsPage;
